import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useTenant } from "@nasam/core";
import { Dialog, DialogClose, DialogContent, DialogTitle, cn } from "@nasam/ui";
import type { NavRailProps } from "./NavRail";
import { resolveIcon } from "./iconRegistry";
import { UserMenu } from "./UserMenu";
import "./MobileNav.css";

interface MobileNavProps extends Pick<
  NavRailProps,
  | "railNodes"
  | "activeRailItemId"
  | "onSelectRailItem"
  | "authController"
  | "authStore"
> {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function MobileNav({
  open,
  onOpenChange,
  railNodes,
  activeRailItemId,
  onSelectRailItem,
  authController,
  authStore,
}: MobileNavProps) {
  const { t } = useTranslation();
  const { branding } = useTenant();
  // The account node's tabs sit in the user dropdown below, not in this list.
  const listNodes = railNodes.filter((node) => node.placement !== "account");
  const accountNode = railNodes.find((node) => node.placement === "account");

  useEffect(() => {
    // Match Tailwind's default md breakpoint; also release modal focus/scroll
    // locks when resizing an open panel onto desktop.
    if (typeof window.matchMedia !== "function") return;
    const desktop = window.matchMedia("(min-width: 768px)");
    const closeOnDesktop = () => {
      if (desktop.matches) onOpenChange(false);
    };
    closeOnDesktop();
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, [open, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* DialogContent supplies DialogPortal + DialogOverlay and Radix Content.
          Override the centered dialog geometry locally for this shell panel. */}
      <DialogContent
        data-node-id="1112:40909"
        aria-describedby={undefined}
        closeLabel={t("common:closeNavigation")}
        showCloseButton={false}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (
            typeof window.matchMedia === "function" &&
            !window.matchMedia("(min-width: 768px)").matches
          ) {
            document
              .querySelector<HTMLButtonElement>(
                '[data-testid="mobile-nav-trigger"]',
              )
              ?.focus();
          }
        }}
        className="mobile-nav-panel start-0 top-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col justify-between gap-0 rounded-none border-0 bg-container-default-background p-0 pt-4 text-text shadow-none rtl:translate-x-0"
      >
        <DialogTitle className="sr-only">
          {t("common:primaryNavigation")}
        </DialogTitle>
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <div className="flex shrink-0 items-center justify-end gap-2 px-2">
            <DialogClose asChild>
              <button
                type="button"
                aria-label={t("common:closeNavigation")}
                className="me-auto flex size-10 shrink-0 items-center justify-center text-secondary-button-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                <X className="size-4" />
              </button>
            </DialogClose>
            {/* A tenant's logo already carries its own wordmark; only the
                default mark needs this text next to it. */}
            {branding?.logoUrl === undefined && (
              <span className="bg-gradient-to-r from-blue-purple-700 via-blue-purple-500 to-blue-purple-900 bg-clip-text text-body-xl-b-ar text-transparent">
                NASAM
              </span>
            )}
            <img
              src={branding?.logoUrl ?? "/brand/logo-mark.png"}
              alt=""
              className="size-14 shrink-0 object-contain"
            />
          </div>
          <nav
            aria-label={t("common:primaryNavigation")}
            className="flex min-h-0 flex-col gap-2 overflow-y-auto px-2 pb-2"
          >
            {listNodes.map((node) => {
              const active = node.id === activeRailItemId;
              const Icon = resolveIcon(node.icon);
              const title = t(
                node.titleKey.includes(":")
                  ? node.titleKey
                  : `navigation:${node.titleKey}`,
              );
              return (
                <button
                  key={node.id}
                  type="button"
                  data-testid={`mobile-nav-item-${node.id}`}
                  aria-current={active ? "page" : undefined}
                  onClick={() => {
                    onOpenChange(false);
                    onSelectRailItem(node);
                  }}
                  className={cn(
                    "flex h-11 w-full shrink-0 items-center justify-end gap-1 rounded-md px-3 py-2 text-body-l-r focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
                    active
                      ? "bg-primary-button-background text-primary-button-text"
                      : "text-secondary-button-text hover:bg-container-hover",
                  )}
                >
                  <span className="truncate">{title}</span>
                  <Icon className="size-4 shrink-0" />
                </button>
              );
            })}
          </nav>
        </div>
        <div className="h-mobile-footer shrink-0 border-t border-container-default-border">
          <UserMenu
            expanded
            authController={authController}
            authStore={authStore}
            accountNode={accountNode}
            onSelectAccountNode={(node) => {
              onOpenChange(false);
              onSelectRailItem(node);
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
